import { useSyncExternalStore } from 'react';
import { api } from '../api';

/**
 * A origem dos cadastros (techspec-indicacoes §03.1). Na primeira carga da página:
 *
 * - `/c/{codigo}` (o link de convite de um jogador) guarda o código e a URL volta para `/` (com a query);
 * - `/p/{codigo}[/{canal}]` (o link de um parceiro, techspec-parceiros §02) vira a campanha do parceiro:
 *   `utm_source = código`, `utm_medium = parceiro`, `utm_campaign = canal`, e a URL volta para `/`;
 * - o código, as `utm_*` e o `document.referrer` vão para `POST /api/origem/visita`, que guarda a visita num cookie
 *   do próprio servidor e devolve o nome de quem convidou (a faixa do cartão da landing).
 *
 * Sem código, sem UTM e sem site de fora, manda só uma vez por sessão do navegador.
 */
const CODIGO = /^[a-z0-9]{3,16}$/;
const MARCA = 'uoidle.origem';

let convite: string | null = null;
const ouvintes = new Set<() => void>();
let preparado = false;

function lerCodigoDoCaminho(): string | null {
  const m = /^\/c\/([^/?#]+)\/?$/i.exec(window.location.pathname);
  if (!m) return null;
  let bruto = '';
  try { bruto = decodeURIComponent(m[1]); } catch { bruto = ''; }
  const codigo = bruto.trim().toLowerCase();
  // A URL do convite vira a página inicial: o resto do site não conhece /c/.
  window.history.replaceState(null, '', `/${window.location.search}${window.location.hash || '#criar'}`);
  return CODIGO.test(codigo) ? codigo : null;
}

const CANAL = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** `/p/lyra` ou `/p/lyra/youtube`: o código e o canal do link do parceiro (o servidor confere os dois). */
function lerParceiroDoCaminho(): { codigo: string; canal: string | null } | null {
  const m = /^\/p\/([^/?#]+)(?:\/([^/?#]+))?\/?$/i.exec(window.location.pathname);
  if (!m) return null;
  const limpar = (v: string | undefined) => {
    try { return decodeURIComponent(v ?? '').trim().toLowerCase(); } catch { return ''; }
  };
  const codigo = limpar(m[1]);
  const canal = limpar(m[2]);
  window.history.replaceState(null, '', `/${window.location.search}${window.location.hash}`);
  if (!CODIGO.test(codigo)) return null;
  return { codigo, canal: canal && canal.length <= 40 && CANAL.test(canal) ? canal : null };
}

function refererDeFora(): string | null {
  const r = document.referrer;
  if (!r) return null;
  try {
    return new URL(r).host === window.location.host ? null : r.slice(0, 2048);
  } catch {
    return null;
  }
}

/** Chamado uma vez, antes da primeira rota (App.tsx). */
export function prepararOrigem(): void {
  if (preparado || typeof window === 'undefined') return;
  preparado = true;
  const codigo = lerCodigoDoCaminho();
  const parceiro = codigo ? null : lerParceiroDoCaminho();
  const q = new URLSearchParams(window.location.search);
  const utm = (k: string) => q.get(k)?.slice(0, 64) || null;
  const visita = parceiro
    ? { codigo: null, utmSource: parceiro.codigo, utmMedium: 'parceiro', utmCampaign: parceiro.canal, referer: refererDeFora() }
    : {
      codigo,
      utmSource: utm('utm_source'),
      utmMedium: utm('utm_medium'),
      utmCampaign: utm('utm_campaign'),
      referer: refererDeFora()
    };
  let jaFoi = false;
  try { jaFoi = sessionStorage.getItem(MARCA) === '1'; } catch { /* sem storage: manda */ }
  if (jaFoi && !visita.codigo && !visita.utmSource && !visita.referer) return;
  try { sessionStorage.setItem(MARCA, '1'); } catch { /* idem */ }
  void api.origemVisita(visita).then(nome => {
    if (!nome) return;
    convite = nome;
    ouvintes.forEach(f => f());
  });
}

/** Quem convidou (o nome do personagem), quando a página abriu por um link de convite válido. */
export function useConvite(): string | null {
  return useSyncExternalStore(
    f => { ouvintes.add(f); return () => { ouvintes.delete(f); }; },
    () => convite,
    () => null
  );
}
